import * as React from 'react';
import { Component } from 'react';
import autobind from 'autobind-decorator';
import classNames from 'classnames';
import * as _ from 'lodash';
import { withRouter } from 'react-router';
import Dropdown from '../../../components/Dropdown';
import Tooltip from '../../../components/Tooltip';
import { __, setTableParams } from '../../../core/utils';
import { hideDropdown } from '../../../redux/actions/general/dropdown';
import {
  deleteTaskDependency,
  updateTaskDependency,
} from '../../../redux/actions/table/all_tasks';
import ArrowDownSmallIcon from '../../../assets/images/arrow-down-small-15x15.svg';
import ArrowDoubleRightSmallIcon from '../../../assets/images/arrow-double-right-small-15x15.svg';
import CloseMiddleIcon from '../../../assets/images/close-middle-15x15.svg';
import styles from '../../../styles/flyouts/tasks/Relations.scss';
import { TaskStatus, TaskTypeIcon } from './TaskRelationVisuals';
import {
  confirmRelationRemoval,
  openRelationTask,
  refreshTaskFlyout,
} from './relationHelpers';
import Cleave from 'cleave.js/dist/cleave-react-node';

const dependencyTypeLabels = {
  FS: 'Finish to Start',
  SS: 'Start to Start',
  FF: 'Finish to Finish',
  SF: 'Start to Finish',
};

class TaskDependencies extends Component {
  constructor(props) {
    super(props);

    this.state = {
      drafts: {},
    };
  }

  _draft(dependency) {
    return {
      type: dependency.type,
      lag_days: dependency.lag_days,
      saving: false,
      ..._.get(this.state.drafts, dependency.id, {}),
    };
  }

  @autobind
  _changeDraft(dependency, changes) {
    this.setState((state) => ({
      drafts: {
        ...state.drafts,
        [dependency.id]: {
          type: dependency.type,
          lag_days: dependency.lag_days,
          saving: false,
          ..._.get(state.drafts, dependency.id, {}),
          ...changes,
        },
      },
    }));
  }

  @autobind
  _save(dependency, changes = {}) {
    const { data, match } = this.props;
    const draft = {
      ...this._draft(dependency),
      ...changes,
    };
    const parsedLagDays = Number.parseInt(draft.lag_days, 10);
    const lagDays = Number.isNaN(parsedLagDays)
      ? Number(dependency.lag_days || 0)
      : parsedLagDays;

    if (draft.type === dependency.type && lagDays === dependency.lag_days) {
      this.setState((state) => ({
        drafts: _.omit(state.drafts, dependency.id),
      }));
      return;
    }

    this._changeDraft(dependency, {
      type: draft.type,
      lag_days: lagDays,
      saving: true,
    });

    updateTaskDependency(dependency.id, {
      type: draft.type,
      lag_days: lagDays,
    })
      .then(() => refreshTaskFlyout(data.id, match))
      .then(() => {
        this.setState((state) => ({
          drafts: _.omit(state.drafts, dependency.id),
        }));
      })
      .catch(() => {
        this.setState((state) => ({
          drafts: {
            ...state.drafts,
            [dependency.id]: {
              type: dependency.type,
              lag_days: dependency.lag_days,
              saving: false,
            },
          },
        }));
      });
  }

  @autobind
  _remove(event, dependencyId) {
    event.stopPropagation();

    const { data, match } = this.props;

    confirmRelationRemoval({
      description: __('task.dependencies.confirm-remove'),
      notification: __('task.dependencies.removed'),
      remove: () => deleteTaskDependency(dependencyId),
      taskId: data.id,
      match,
    });
  }

  _columns() {
    return (
      <div className={styles.dependencyColumns}>
        <span className={styles.task}>{__('table.columns.task')}</span>
        <span className={styles.type}>{__('task.dependencies.type')}</span>
        <span className={styles.lag}>{__('task.dependencies.lag-days')}</span>
        <span className={styles.status}>{__('table.columns.status')}</span>
        <span className={styles.actions} />
      </div>
    );
  }

  _dependencyRow(dependency, index, direction) {
    const { match } = this.props;
    const task = dependency.task;
    const draft = this._draft(dependency);

    return (
      <div
        className={classNames(
          styles.row,
          styles.dependencyRow,
          draft.saving && styles.saving
        )}
        key={`${direction}-${dependency.id}-${index}`}
      >
        <div className={styles.dependencyTask}>
          <TaskTypeIcon type={task.type} />
          <div
            onClick={() => openRelationTask(task, match)}
            className={classNames(
              styles.link,
              task.can_view ? styles.clickable : styles.disabled
            )}
          >
            {!task.can_view && (
              <Tooltip text={__('task.dependencies.private-task-no-access')}>
                {this._taskLabel(task)}
              </Tooltip>
            )}
            {task.can_view && this._taskLabel(task)}{' '}
            {task.can_view && <ArrowDoubleRightSmallIcon />}
          </div>
        </div>
        <div className={styles.dependencyTypeEditor}>
          <Dropdown
            top={32}
            leftStyled
            portal
            closeOnScroll
            readOnly={draft.saving || !task.can_view}
            wrapperClassName={styles.dependencyTypeDropdown}
            dropdownClassName={styles.dependencyTypeMenu}
            name={`task_dependency_type_${dependency.id}`}
            header={
              <div className={styles.dependencyTypeMenuHeader}>
                {__('task.dependencies.type')}
              </div>
            }
            content={
              <div className={styles.dependencyTypeMenuContent}>
                {_.map(dependencyTypeLabels, (label, type) => (
                  <div
                    className={classNames(
                      styles.dependencyTypeOption,
                      draft.type === type && styles.active
                    )}
                    key={type}
                    title={label}
                    onClick={(event) => {
                      event.stopPropagation();
                      hideDropdown();

                      if (draft.type === type) {
                        return;
                      }

                      this._changeDraft(dependency, { type });
                      this._save(dependency, { type });
                    }}
                  >
                    {type}
                  </div>
                ))}
              </div>
            }
          >
            <div
              aria-label={__('task.dependencies.type')}
              className={classNames(
                styles.dependencyTypeTrigger,
                (draft.saving || !task.can_view) && styles.disabled
              )}
              title={dependencyTypeLabels[draft.type]}
            >
              <span>{draft.type}</span>
              <ArrowDownSmallIcon />
            </div>
          </Dropdown>
        </div>
        <div className={styles.dependencyLagEditor}>
          <Cleave
            options={{
              numericOnly: true,
            }}
            onBlur={() => this._save(dependency)}
            onFocus={() =>
              this.props.onFocus ? this.props.onFocus() : this._handleOnFocus()
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur();
              }
            }}
            value={draft.lag_days}
            onChange={(event) =>
              this._changeDraft(dependency, {
                lag_days: event.currentTarget.value,
              })
            }
            disabled={draft.saving || !task.can_view}
            type={'number'}
          />

          {/* <input
            aria-label={__(
              'task.dependencies.lag-working-days'
            )}
            disabled={draft.saving || !task.can_view}
            type='number'
            step='1'
            value={draft.lag_days}
            onChange={(event) =>
              this._changeDraft(dependency, {
                lag_days: event.currentTarget.value,
              })
            }
            onBlur={() => this._save(dependency)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur();
              }
            }}
          /> */}
          <span>d</span>
        </div>
        <div className={styles.dependencyStatus}>
          <TaskStatus status={task.status} />
        </div>
        <div className={styles.actions}>
          {task.can_view && (
            <Tooltip text={__('task.dependencies.remove')}>
              <CloseMiddleIcon
                onClick={(event) => this._remove(event, dependency.id)}
              />
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  _taskLabel(task) {
    return (
      <span>
        <span className={styles.dependencyIdentifier}>
          {'T-' + task.identifier}
        </span>{' '}
        &middot; {task.title}
      </span>
    );
  }

  _dependencyGroup(direction, dependencies) {
    const { data } = this.props;
    const isPredecessor = direction === 'predecessor';

    return (
      <div className={styles.dependencyGroup}>
        <div className={styles.dependencyHeader}>
          <div className={styles.f1}>
            <label className={styles.label}>
              {isPredecessor
                ? __('tasks.all-tasks.flyout.related-tasks.predecessors')
                : __('tasks.all-tasks.flyout.related-tasks.successors')}
            </label>
            <div className={styles.pathDescription}>
              {isPredecessor
                ? __(
                    'tasks.all-tasks.flyout.related-tasks.predecessors-description'
                  )
                : __(
                    'tasks.all-tasks.flyout.related-tasks.successors-description'
                  )}
            </div>
          </div>
          <div className={styles.headerActions}>
            <button
              onClick={() =>
                setTableParams('all_tasks', {
                  add_task_dependency_wizard: {
                    task_id: data.id,
                    direction,
                  },
                })
              }
            >
              {isPredecessor
                ? __('button.add-predecessor')
                : __('button.add-successor')}
            </button>
          </div>
        </div>
        {!_.isEmpty(dependencies) && (
          <>
            {this._columns()}
            {_.map(dependencies, (dependency, index) =>
              this._dependencyRow(dependency, index, direction)
            )}
          </>
        )}
      </div>
    );
  }

  render() {
    const { data } = this.props;

    if (data.type !== 'group') {
      return null;
    }

    const predecessors = _.get(data, 'dependencies.predecessors', []);
    const successors = _.get(data, 'dependencies.successors', []);

    return (
      <>
        <div className={styles.dividerLine} />
        <div className={styles.sectionHeader}>
          <div className={styles.f1}>
            <label className={styles.label}>
              {__('tasks.all-tasks.flyout.related-tasks.dependencies')}
            </label>
            <div className={styles.pathDescription}>
              {__(
                'tasks.all-tasks.flyout.related-tasks.dependencies-description'
              )}
            </div>
          </div>

          {this._dependencyGroup('predecessor', predecessors)}
          {this._dependencyGroup('successor', successors)}
        </div>
      </>
    );
  }
}

export default withRouter(TaskDependencies);
